import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { Vitals } from '../types';

interface VitalsChartProps {
  vitals: Vitals[];
  targetBmi?: number;
  onTargetBmiChange?: (target: number) => void;
}

const VitalsChart: React.FC<VitalsChartProps> = ({ vitals, targetBmi, onTargetBmiChange }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!vitals || vitals.length === 0 || !chartRef.current) return;

    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 20, right: 40, bottom: 30, left: 40 };
    const width = chartRef.current.clientWidth - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Parse dates and sort just in case
    const data = vitals.map(v => ({
      date: new Date(v.recordedAt),
      weight: v.weight,
      bmi: v.bmi
    })).sort((a, b) => a.date.getTime() - b.date.getTime());

    // X Scale
    const x = d3.scaleTime()
      .domain(d3.extent(data, d => d.date) as [Date, Date])
      .range([0, width]);

    // Y Scales
    const yWeightDomain = [d3.min(data, d => d.weight)! - 5, d3.max(data, d => d.weight)! + 5];
    const yWeight = d3.scaleLinear()
      .domain(yWeightDomain)
      .range([height, 0]);

    let bmiMin = d3.min(data, d => d.bmi)!;
    let bmiMax = d3.max(data, d => d.bmi)!;
    if (targetBmi !== undefined) {
      bmiMin = Math.min(bmiMin, targetBmi);
      bmiMax = Math.max(bmiMax, targetBmi);
    }

    const yBmi = d3.scaleLinear()
      .domain([bmiMin - 2, bmiMax + 2])
      .range([height, 0]);

    // Axes
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat(d3.timeFormat("%b %d") as any))
      .attr('color', '#64748b');

    svg.append('g')
      .call(d3.axisLeft(yWeight))
      .attr('color', '#3b82f6');

    svg.append('g')
      .attr('transform', `translate(${width},0)`)
      .call(d3.axisRight(yBmi))
      .attr('color', '#f59e0b');

    // Goal Line for Target BMI
    if (targetBmi !== undefined && data.length > 0) {
      svg.append('line')
        .attr('x1', 0)
        .attr('x2', width)
        .attr('y1', yBmi(targetBmi))
        .attr('y2', yBmi(targetBmi))
        .attr('stroke', '#10b981')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5');
        
      svg.append('text')
        .attr('x', width - 5)
        .attr('y', yBmi(targetBmi) - 5)
        .attr('fill', '#10b981')
        .attr('font-size', '10px')
        .attr('text-anchor', 'end')
        .attr('font-weight', 'bold')
        .text('Target BMI');
    }

    // Lines
    const weightLine = d3.line<{date: Date, weight: number, bmi: number}>()
      .x(d => x(d.date))
      .y(d => yWeight(d.weight))
      .curve(d3.curveMonotoneX);

    const bmiLine = d3.line<{date: Date, weight: number, bmi: number}>()
      .x(d => x(d.date))
      .y(d => yBmi(d.bmi))
      .curve(d3.curveMonotoneX);

    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#3b82f6')
      .attr('stroke-width', 2)
      .attr('d', weightLine);

    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#f59e0b')
      .attr('stroke-width', 2)
      .attr('d', bmiLine);

    // Points for Weight
    svg.selectAll(".dot-weight")
      .data(data)
      .enter().append("circle")
      .attr("class", "dot-weight")
      .attr("cx", d => x(d.date))
      .attr("cy", d => yWeight(d.weight))
      .attr("r", 4)
      .attr("fill", "#3b82f6");

    // Points for BMI
    svg.selectAll(".dot-bmi")
      .data(data)
      .enter().append("circle")
      .attr("class", "dot-bmi")
      .attr("cx", d => x(d.date))
      .attr("cy", d => yBmi(d.bmi))
      .attr("r", 4)
      .attr("fill", "#f59e0b");

  }, [vitals, targetBmi]);

  if (!vitals || vitals.length < 2) {
    return (
      <div className="text-center text-slate-500 text-xs p-8 bg-slate-50 rounded-lg border border-slate-100">
        Not enough vitals data to plot history (requires at least 2 entries).
      </div>
    );
  }

  return (
    <div className="bg-white border text-sm border-slate-200 rounded-lg p-4 w-full">
      <div className="flex justify-between items-center mb-2">
        <h4 className="font-bold text-slate-700 text-sm">Weight & BMI Timeline</h4>
        <div className="flex items-center gap-4">
          <div className="flex gap-4 text-xs font-semibold mr-4">
            <span className="text-blue-500 flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500"></div> Weight (kg)</span>
            <span className="text-amber-500 flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-amber-500"></div> BMI</span>
            {targetBmi !== undefined && (
              <span className="text-[#10b981] flex items-center gap-1"><div className="w-2 h-0.5 border-t-2 border-dashed border-[#10b981]"></div> Target BMI</span>
            )}
          </div>
          {onTargetBmiChange && (
            <div className="flex items-center gap-2 border border-slate-200 rounded px-2 py-1 bg-slate-50">
              <span className="text-xs text-slate-500 font-bold uppercase">Target:</span>
              <input 
                type="number" 
                defaultValue={targetBmi || ''}
                onBlur={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val)) onTargetBmiChange(val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const val = parseFloat(e.currentTarget.value);
                    if (!isNaN(val)) {
                      e.currentTarget.blur();
                      onTargetBmiChange(val);
                    }
                  }
                }}
                className="w-14 text-xs font-semibold bg-white border border-slate-200 rounded px-1 py-0.5 outline-none focus:border-indigo-500"
                placeholder="BMI"
              />
            </div>
          )}
        </div>
      </div>
      <div ref={chartRef} className="w-full h-[300px]"></div>
    </div>
  );
};

export default VitalsChart;
